import { Download, Target } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PLAN_PDF = "fondation-hora/uploads/plans/plan-action-2025-2026-f49d906c-63e4-4783-aa18-8f9c2c021804.pdf";

const axes = [
  {
    num: "01",
    titre: "Éducation et sensibilisation juridique",
    couleur: "border-gold",
    actions: [
      { nom: "Formations et ateliers", trimestre: "2e trimestre", objectif: "12 ateliers par an, sur la gestion d'entreprise, les risques de faillite et la restructuration, pour les entrepreneurs, OBNL et PME." },
      { nom: "Webinaires et guides pratiques en ligne", trimestre: "2e trimestre", objectif: "Des ressources en ligne pour comprendre ses droits et obligations légales et anticiper les difficultés financières." },
      { nom: "Bourses d'excellence", trimestre: "4e trimestre", objectif: "Soutenir des étudiants en droit intéressés par la faillite et la restructuration d'entreprises, pour favoriser la relève." },
    ],
  },
  {
    num: "02",
    titre: "Engagement communautaire et développement de partenariats",
    couleur: "border-primary",
    actions: [
      { nom: "Programmes d'incubation pour les OBNL et PME", trimestre: "1er au 4e trimestre", objectif: "Un accompagnement stratégique de 12 mois pour les entreprises à risque." },
      { nom: "Partenariats stratégiques", trimestre: "2e trimestre", objectif: "Des alliances avec les chambres de commerce, les organisations de soutien et les banques pour co-organiser des événements de sensibilisation et de financement." },
      { nom: "Journées de consultation gratuite", trimestre: "4e trimestre", objectif: "Des cliniques juridiques où les entrepreneurs posent gratuitement leurs questions à des avocats et des experts." },
    ],
  },
  {
    num: "03",
    titre: "Renforcement des capacités internes",
    couleur: "border-espresso",
    actions: [
      { nom: "Campagnes de lancement", trimestre: "1er trimestre", objectif: "Faire connaître nos services et nos programmes auprès des communautés que nous servons." },
      { nom: "Recrutement du personnel et des bénévoles", trimestre: "3e trimestre", objectif: "5 nouveaux avocats spécialisés en droit des faillites, restructuration et droit des affaires d'ici 12 mois." },
      { nom: "Formations continues", trimestre: "En continu", objectif: "Former régulièrement nos avocats et professionnels sur les nouvelles législations, la gestion de crise et les stratégies de restructuration." },
      { nom: "Outils technologiques", trimestre: "En continu", objectif: "Un système de gestion électronique des dossiers pour plus d'efficacité et un suivi en temps réel." },
    ],
  },
  {
    num: "04",
    titre: "Accompagnement et soutien des entreprises en difficulté",
    couleur: "border-gold",
    actions: [
      { nom: "Diagnostic d'entreprise", trimestre: "1er trimestre", objectif: "Des diagnostics gratuits ou à tarif réduit pour évaluer la situation des entreprises en difficulté et proposer des stratégies de redressement." },
      { nom: "Plan de restructuration personnalisé", trimestre: "2e trimestre", objectif: "Des plans avec suivi mensuel et trimestriel des progrès — au moins 100 entreprises aidées chaque année." },
      { nom: "Mise en réseau", trimestre: "4e trimestre", objectif: "Connecter les entreprises accompagnées à un réseau de partenaires financiers, consultants et experts en gestion." },
    ],
  },
];

export default function PlanAction() {
  return (
    <>
      <section className="bg-espresso py-28 text-ivory md:py-36" data-testid="plan-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Plan d'action · 2025-2026</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="plan-title">
              Nos quatre chantiers pour <span className="italic text-primary">l'année</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Élaboré à l'époque de la Clinique juridique d'affaires et poursuivi par la Fondation
              HORA, notre plan d'action 2025-2026 structure nos actions en quatre axes, avec des
              objectifs mesurables et un échéancier par trimestre.
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/media/${PLAN_PDF}`}
              target="_blank"
              rel="noopener"
              data-testid="plan-pdf-button"
              className="group mt-10 inline-flex items-center gap-3 rounded-sm border border-ivory/25 px-8 py-4 text-sm font-semibold text-ivory transition-colors duration-200 hover:border-gold hover:text-gold"
            >
              <Download size={16} />
              Consulter le document original (PDF)
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="plan-axes">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="space-y-20">
            {axes.map((axe, i) => (
              <Reveal key={axe.num} delay={0.05}>
                <div className={`grid gap-10 border-t-4 ${axe.couleur} pt-10 lg:grid-cols-12`} data-testid={`plan-axe-${i}`}>
                  <div className="lg:col-span-4">
                    <span className="font-serif text-6xl font-medium text-gold md:text-7xl">{axe.num}</span>
                    <h2 className="mt-4 font-serif text-3xl font-semibold leading-snug">{axe.titre}</h2>
                  </div>
                  <div className="space-y-0 lg:col-span-8">
                    {axe.actions.map((a, j) => (
                      <div key={a.nom} className="border-t border-border py-6 first:border-t-0 first:pt-0 lg:first:pt-6" data-testid={`plan-action-${i}-${j}`}>
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="font-serif text-xl font-semibold">{a.nom}</h3>
                          <span className="rounded-sm bg-muted px-3 py-1 text-xs font-semibold text-foreground/60" data-testid={`plan-trimestre-${i}-${j}`}>
                            {a.trimestre}
                          </span>
                        </div>
                        <p className="mt-2 flex items-start gap-3 text-sm leading-relaxed text-foreground/60 md:text-base">
                          <Target size={15} className="mt-1 shrink-0 text-primary" />
                          {a.objectif}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
